/**
 * Sprint P20 · Feb 2026 · Competent Person Workspace (P0.5).
 *
 * Six tabs, each rendered by a small sibling component (< 200 LOC each).
 *   1. Profiles          — CPProfilesPanel
 *   2. Responsibilities  — CPResponsibilitiesPanel
 *   3. Site Visits       — CPSiteVisitsPanel
 *   4. Documents         — CPDocumentsPanel
 *   5. Declaration       — CPDeclarationPanel  (generate · edit · sign · lock · insert)
 *   6. Compliance Audit  — CPComplianceAuditPanel  (P0.1 39-section matrix)
 *
 * NB: mounted on the CPR Generate step so the full audit ↔ declaration ↔
 * report insertion flow lives in one place.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  IdentificationCard, ClipboardText, MapTrifold, Files, Scroll, ShieldCheck,
} from "@phosphor-icons/react";
import { WizardCard, KV, LoadingBanner, StatusRow } from "@/pages/wizard/_shared";
import { getCPSummary } from "@/lib/api";
import CPProfilesPanel from "@/pages/wizard/cp/CPProfilesPanel";
import CPResponsibilitiesPanel from "@/pages/wizard/cp/CPResponsibilitiesPanel";
import CPSiteVisitsPanel from "@/pages/wizard/cp/CPSiteVisitsPanel";
import CPDocumentsPanel from "@/pages/wizard/cp/CPDocumentsPanel";
import CPDeclarationPanel from "@/pages/wizard/cp/CPDeclarationPanel";
import CPComplianceAuditPanel from "@/pages/wizard/cp/CPComplianceAuditPanel";


const TABS = [
  { key: "profiles",         label: "Profiles",         icon: IdentificationCard,      Cmp: CPProfilesPanel },
  { key: "responsibilities", label: "Responsibilities", icon: ClipboardText,           Cmp: CPResponsibilitiesPanel },
  { key: "visits",           label: "Site Visits",      icon: MapTrifold,              Cmp: CPSiteVisitsPanel },
  { key: "documents",        label: "Documents",        icon: Files,                   Cmp: CPDocumentsPanel },
  { key: "declaration",      label: "Declaration",      icon: Scroll,                  Cmp: CPDeclarationPanel },
  { key: "audit",            label: "Compliance Audit", icon: ShieldCheck,             Cmp: CPComplianceAuditPanel },
];


export default function CompetentPersonWorkspace({ projectId }) {
  const [tab, setTab] = useState("profiles");
  const summaryQ = useQuery({
    queryKey: ["cp-summary", projectId],
    queryFn:  () => getCPSummary(projectId),
    enabled:  !!projectId,
    refetchInterval: 30_000,
  });

  const s = summaryQ.data || {};
  const ActiveCmp = TABS.find((t) => t.key === tab)?.Cmp || TABS[0].Cmp;

  return (
    <WizardCard
        title="Competent Person Workspace"
        subtitle={`Reporting standard: ${s.reporting_standard || "JORC"} · zero fake data · signed declarations are locked`}
        testId="w-cp-workspace">
      {summaryQ.isLoading && <LoadingBanner label="Loading Competent Person state…" />}
      {summaryQ.isError && (
        <StatusRow tone="danger">
          Failed to load CP summary — check backend logs.
        </StatusRow>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        <KV k="Profiles"          v={s.profiles ?? 0}          testId="w-cp-kpi-profiles" />
        <KV k="Assignments"       v={s.responsibilities ?? 0}  testId="w-cp-kpi-resp" />
        <KV k="Site visits"       v={s.site_visits ?? 0}       testId="w-cp-kpi-visits" />
        <KV k="Documents"         v={s.documents ?? 0}         testId="w-cp-kpi-docs" />
        <KV k="Signed declarations" v={s.signed_declarations ?? 0} testId="w-cp-kpi-signed" />
        <KV k="Drafts"            v={s.draft_declarations ?? 0} testId="w-cp-kpi-drafts" />
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-3">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.key === tab;
          return (
            <button key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`w-cp-tab-${t.key}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-mono border-b-2 transition ${
                  active
                    ? "border-brand-blue text-brand-blue"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}>
              <Icon size={11} className="inline mr-1 -mt-0.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <ActiveCmp projectId={projectId} summary={s} onRefresh={() => summaryQ.refetch()} />
    </WizardCard>
  );
}
